import PropTypes from 'prop-types';
import React, { useReducer, useMemo, useEffect } from 'react';

import ValidationContext from './ValidationContext.ts';
import useEventCallback from '../utils/useEventCallback/index.ts';
import usePrevious from '../utils/usePrevious/index.ts';
import uuid from '../utils/uuid/index.ts';

const ACTION_TYPES = {
  REGISTER: 'REGISTER',
  UNREGISTER: 'UNREGISTER',
  UPDATE_INPUT: 'UPDATE_INPUT'
};

function reducer(currentInputs, action) {
  switch (action.type) {
    case ACTION_TYPES.REGISTER: {
      const { inputId, isValid } = action;

      return { ...currentInputs, [inputId]: isValid };
    }

    case ACTION_TYPES.UNREGISTER: {
      const { inputId } = action;
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { [inputId]: inputToRemove, ...nextInputs } = currentInputs;

      return nextInputs;
    }

    case ACTION_TYPES.UPDATE_INPUT: {
      const { inputId, isValid } = action;
      if (currentInputs[inputId] === isValid) {
        return currentInputs;
      }

      return {
        ...currentInputs,
        [inputId]: isValid
      };
    }
  }

  return currentInputs;
}

function Form({ children, onChange: onChangeFromProps }) {
  const [registeredInputs, dispatch] = useReducer(reducer, {});
  const areAllValid = useMemo(
    () => Object.values(registeredInputs).every(isInputValid => isInputValid),
    [registeredInputs]
  );
  const prevAllValid = usePrevious(areAllValid);
  const getIsValid = useEventCallback(() => areAllValid);
  const childrenEl = typeof children === 'function' ? children(!areAllValid) : children;
  const contextValue = useMemo(
    () => ({
      register(isValid) {
        const inputId = uuid();

        dispatch({ type: ACTION_TYPES.REGISTER, inputId, isValid });

        return inputId;
      },
      deregister(inputId) {
        dispatch({ type: ACTION_TYPES.UNREGISTER, inputId });
      },
      update(inputId, isValid) {
        dispatch({ type: ACTION_TYPES.UPDATE_INPUT, inputId, isValid });
      },
      getIsValid
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );
  const onChange = useEventCallback(onChangeFromProps);

  useEffect(() => {
    if (!prevAllValid !== !areAllValid || prevAllValid === undefined) {
      onChange(!areAllValid);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [areAllValid, prevAllValid]);

  return <ValidationContext.Provider value={contextValue}>{childrenEl}</ValidationContext.Provider>;
}

Form.propTypes = {
  /**
   * The inner elements and inputs of the forms
   * if a function called with `hasErrors`
   */
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.func]).isRequired,
  /**
   * Fired on change of `hasErrors`
   */
  onChange: PropTypes.func
};

Form.defaultProps = {
  onChange: () => {}
};

export default Form;
